import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  AddEntityToCampaignGQL, CampaignEntityTypeConfigDetails, CampaignEntityTypeConfigDetailsFragment,
  CreateEntityGQL, EntityCore, GetCampaignByIdNoTags, GetCampaignByIdNoTagsGQL,
  GetCampaignEntityTypeConfigurationsGQL, Language, SearchEntitiesPublic
} from 'generated/graphql';

import { CustomFormBuilderServiceFactory } from 'app/custom-forms/CustomFormBuilder.service';
import { CustomFormSubmissionService } from 'app/custom-forms/CustomFormSubmission.service';
import { removeControlsFromFormArray } from 'core/angular-extensions';
import { LANGUAGES } from 'core/languages';
import { WithCompareByIdFn } from 'core/mixins/with-compare-by-id-fn';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import {
  PotentialDuplicateDialog
} from 'entity/dialogs/potential-duplicate/potential-duplicate.dialog';
import { EmailFormModel } from 'entity/forms/email-form/email-form.component';
import { NameFormModel } from 'entity/forms/name-form/name-form.component';
import { PhoneFormModel } from 'entity/forms/phone-form/phone-form.component';
import { isNilOrEmpty } from 'helpers';
import { AddressFormComponent, AddressFormModel } from 'shared/forms/address-form/address-form.component';

import { EntityDuplicatesComponent } from '../entity-duplicates/entity-duplicates.component';
import { translateToCreateEntityInput } from './translate-to-create-entity-input';
import { isInternationalCountry } from 'core/geography';
import { LanguageFormComponent } from 'entity/forms/language-form/language-form.component';


export interface EntityAddedEvent {
  addAnother: boolean;
  entity: EntityCore.Fragment;
}
const PANEL_NAMES = {
  NAME: 'name',
  EMAILS: 'emails',
  PHONES: 'phones',
  ADDRESSES: 'addresses'
};

@Component({
  selector: 'app-create-entity',
  templateUrl: './create-entity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateEntityComponent
  extends WithDisposableSubject(WithCompareByIdFn(class { }))
  implements OnInit {
  /** Required campaignId  */
  @Input() campaignId: string;

  /**
   * Optional input to set the initially selected
   * entity type.
   * If not provided, will default to the default
   * entity type for the campaignId provided
   */
  @Input() initialEntityTypeId: string;

  /** Fired after an entity has been created / added to the campaign */
  @Output() entityAdded: EventEmitter<EntityAddedEvent> = new EventEmitter<EntityAddedEvent>();

  @ViewChild(EntityDuplicatesComponent, { static: false }) entityDuplicatesComponent: EntityDuplicatesComponent;
  @ViewChildren(AddressFormComponent) addressFormComponents: QueryList<AddressFormComponent>;


  PANEL_NAMES = PANEL_NAMES;

  isSaving = false;
  isSavingTags: boolean;
  creationStatusMessage: string;
  // Used to calculatue the progress of creation
  // Incremented each time a response or entity is created
  itemsToCreate = 1;

  // Calculated value of the progress
  createProgress: number;

  defaultCountry: string;

  collapsedPanels: string[] = [];

  /** Default name panel is open */
  expandedPanel = '';

  campaign: GetCampaignByIdNoTags.GetCampaign;
  //entityTypes: EntityTypeDetails.Fragment[];
  entityTypeConfigs: CampaignEntityTypeConfigDetailsFragment[];
  selectedEntityTypeConfig: CampaignEntityTypeConfigDetailsFragment;
  get selectedEntityType() {
    return this.selectedEntityTypeConfig?.entityType;
  }

  // Form Controls
  nameInfoFC = new UntypedFormControl();
  emailsFA = new UntypedFormArray([]);
  phonesFA = new UntypedFormArray([]);
  addressesFA = new UntypedFormArray([]);
  entityLanguages = new FormControl([{
    languageCode: Language.En,
    preferred: true
  }]);

  formGroup: UntypedFormGroup; // root form group
  customFormGroup: UntypedFormGroup;
  entityTypeConfig: CampaignEntityTypeConfigDetails.Fragment;


  get firstNameInvalid() {
    return isNilOrEmpty(this.nameInfoFC?.value?.firstName)
  }

  /** Internal */
  constructor(
    private _toastrService: ToastrService,
    private _getCampaignGQL: GetCampaignByIdNoTagsGQL,
    private _addEntityToCampaign: AddEntityToCampaignGQL,
    private _getEntityTypeConfigsGQL: GetCampaignEntityTypeConfigurationsGQL,
    private _createEntityGQL: CreateEntityGQL,
    private _customFormSubmissionService: CustomFormSubmissionService,
    private _dynamicFormService: CustomFormBuilderServiceFactory,
    private _dialog: MatDialog,
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this._getCampaignGQL.fetch(
      { campaignId: this.campaignId }
    ).pipe(
      tap((result) => {
        this.campaign = result.data.getCampaign;
        this.defaultCountry = this.campaign.defaultCountry;
      }),
      switchMap((res) => this._getEntityTypeConfigsGQL.fetch({
        campaignId: this.campaignId
      }).pipe(
        map(({ data }) => data.getCampaignEntityTypeConfigurations)
      ))
    ).subscribe(
      (result) => {
        this.entityTypeConfigs = result;
        let initialEntityType = null;
        if (this.initialEntityTypeId) {
          initialEntityType = this.campaign.entityTypes.find(et => et.id === this.initialEntityTypeId);
        } else {
          initialEntityType = this.campaign.defaultEntityType;
        }

        if (initialEntityType) {
          this.setSelectedEntityTypeConfig(this.entityTypeConfigs.find(etc => etc.entityType.id === initialEntityType.id));
        }

        this.formGroup = new UntypedFormGroup({
          emails: this.emailsFA,
          phones: this.phonesFA,
          addresses: this.addressesFA,
          nameInfo: this.nameInfoFC,
          entityLanguages: this.entityLanguages
        });

        // Needed to prevent error after check on formGroup.valid
        // Seems to be an angular bug, could also be I don't understand
        // what is happening internally with the ControlValueAccessors
        this._cd.detectChanges();

        // Attach a listener to search for duplicates
        // to the name form control
        this._attachDuplicatesSearchTrigger();

      });
  }

  setSelectedEntityTypeConfig(entityTypeConfig: CampaignEntityTypeConfigDetailsFragment) {
    if (!entityTypeConfig) {
      this.selectedEntityTypeConfig = null;
      this.customFormGroup = new UntypedFormGroup({});
      return;
    }

    this.selectedEntityTypeConfig = entityTypeConfig;
    this.customFormGroup =
      this._dynamicFormService
        .createFormBuilder(this.selectedEntityTypeConfig.creationCustomForm, this.selectedEntityType, true)
        .createFormGroup();

    // Update view on model change
    this.customFormGroup.valueChanges.pipe(
      takeUntil(this.disposer),
      debounceTime(500)
    ).subscribe(
      (_val) => {
        this._cd.markForCheck();
      }
    );

    this._cd.markForCheck();
  }

  private _attachDuplicatesSearchTrigger() {
    this.nameInfoFC.valueChanges
      .pipe(
        takeUntil(this.disposer),
        filter(f => !!f)
      )
      .subscribe((v: NameFormModel) => {
        this.entityDuplicatesComponent?.search(
          {
            firstName: v.firstName,
            lastName: v.lastName,
            entityTypeId: this.selectedEntityType?.id,
            assessmentCampaignId: this.campaignId
          }
        );
      });
  }

  onDuplicateSelected(duplicate: SearchEntitiesPublic.Nodes) {
    const dref = this._dialog.open(PotentialDuplicateDialog, {
      data: {
        entity: duplicate,
        campaignId: this.campaignId
      }
    });

    dref.afterClosed().pipe(
      // Only act when we get a positive result
      filter(result => result),
      switchMap((result: { id }) => this._addEntityToCampaign.mutate({
        input: {
          entityId: result.id,
          newCampaignId: this.campaignId
        }
      }).pipe(
        map(res => res?.data.addEntityToCampaign?.entity)
      )),
    )
      .subscribe(result => {
        // Close the add person modal if duplicate was selected
        if (result) {
          this.entityAdded.emit({ entity: result, addAnother: false });
        }
      });

  }

  panelExpanded(panelName: string) {
    this._addNewFCIfEmpty(panelName);
    this.collapsedPanels = this.collapsedPanels.filter(n => n !== panelName);
    this.expandedPanel = panelName;
    this._cd.markForCheck();
  }

  panelCollapsed(panelName: string) {
    this._removeEmptyFCsInPanel(panelName);
    if (!this.collapsedPanels.includes(panelName)) {
      this.collapsedPanels = [...this.collapsedPanels, panelName];
    }
    this._cd.markForCheck();
  }

  isPanelCollapsed(panelName: string) {
    return this.collapsedPanels.includes(panelName);
  }

  /**
   * Adds a new control to the given form array
   *
   * @param formArray FormArray to add control
   * @param fcValue initial value for control
   */
  addToFormArray(formArray: UntypedFormArray, fcValue: any = null) {
    const fc = new UntypedFormControl();
    formArray.push(fc);
    fc.patchValue(fcValue);

    // Needed to prevent error changes after check on formGroup.valid
    this._cd.detectChanges();
  }

  /**
   *
   * @param index Index to remove
   * @param formArray FormArray to pull from
   */
  removeFromFormArray(formArray: UntypedFormArray, index: number) {
    formArray.removeAt(index);
  }

  /**
   * "Create & Add Another" click handler.
   */
  onCreateAndAddAnother() {
    this.onSubmitClick(true);
  }

  /**
   * Submit click handler.
   */
  onSubmitClick(addAnother: boolean = false) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    // Remove any "empty" form items that the user didn't fill out
    [
      PANEL_NAMES.ADDRESSES,
      PANEL_NAMES.EMAILS,
      PANEL_NAMES.PHONES
    ].forEach(panel => this._removeEmptyFCsInPanel(panel));

    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();

    if (this.addressFormComponents) {
      this.addressFormComponents.forEach(comp => comp.markAllAsTouched());
    }

    if (!this.formGroup.valid) {
      this._toastrService.error('There are some errors on your form.');
      this.isSaving = false;
    }

    if (!this.nameInfoFC.valid || isNilOrEmpty(this.nameInfoFC?.value?.firstName)) {
      this.expandedPanel = this._firstInvalidPanelName();
      this.isSaving = false;
      return;
    }

    if (!this.formGroup.valid) {
      this.isSaving = false;
      return;
    }

    const input = translateToCreateEntityInput(this.formGroup.value, this.selectedEntityType.id, this.campaignId);

    this.itemsToCreate = CustomFormSubmissionService
      .NumberOfItemsToCreate(this.customFormGroup, this.selectedEntityTypeConfig.creationCustomForm) + 1;

    this._setCreationStatusMessage('Creating Entity...', .5);

    this._createEntityGQL.mutate({ input })
      .pipe(
        map(t => t.data.createEntity.entity),
        tap(_ => {
          this._setCreationStatusMessage('Entity Created', 1);
        }),
        switchMap(
          (entity) => this.submitCustomForm(entity)
        ),
        finalize(() => {
          this.isSaving = false
        })
      ).subscribe(entity => {
        this._cd.markForCheck();
        this.entityAdded.emit({ entity, addAnother });
      });
  }

  submitCustomForm(entity: EntityCore.Fragment) {
    const applier = this._customFormSubmissionService.createApplierForEntity(
      this.customFormGroup,
      this.selectedEntityTypeConfig.creationCustomForm,
      entity
    );

    applier.progress$.pipe(
      takeUntil(this.disposer)
    ).subscribe(progress => {
      if (progress.toastLevel) {
        this._toastrService[progress.toastLevel](progress.statusMessage);
      }
      this.creationStatusMessage = progress.statusMessage;
      const itemsCreated = 1 + progress.itemsCreated;
      this.createProgress = (itemsCreated / (progress.itemsToCreate + 1)) * 100;
      console.log(this.createProgress, itemsCreated, progress.itemsToCreate);
      this._cd.markForCheck();
    });

    return applier.applyForm();
  }

  private _setCreationStatusMessage(message: string, itemsCreated: number) {
    this.creationStatusMessage = message;
    this.createProgress = (itemsCreated / this.itemsToCreate) * 100;
    this._cd.markForCheck();
  }

  /**
   * Returns the name of the first invalid panel
   * If all panels are valid, returns undefined.
   */
  private _firstInvalidPanelName() {
    if (!this.nameInfoFC.valid) {
      return PANEL_NAMES.NAME;
    }
    if (!this.emailsFA.valid) {
      return PANEL_NAMES.EMAILS;
    }
    if (!this.phonesFA.valid) {
      return PANEL_NAMES.PHONES;
    }
    if (!this.addressesFA.valid) {
      return PANEL_NAMES.ADDRESSES;
    }
  }

  /**
   * A little helper method that will automatically
   * add a new empty item to either emails/phones/addresses
   * when expanded.
   *
   * @param panelName The name of the panel
   */
  private _addNewFCIfEmpty(panelName: string) {
    const fa = this._getFAForPanel(panelName);
    if (fa === null || fa.controls.length !== 0) {
      return;
    }
    if (panelName === PANEL_NAMES.EMAILS) {
      this.addToFormArray(fa);
    }
    if (panelName === PANEL_NAMES.ADDRESSES) {
      this.addToFormArray(fa, { country: this.defaultCountry });
    }
    if (panelName === PANEL_NAMES.PHONES) {
      this.addToFormArray(fa);
    }
  }

  /**
   * Returns the form array for a given panel name
   */
  private _getFAForPanel(panelName: string) {
    switch (panelName) {
      case PANEL_NAMES.EMAILS:
        return this.emailsFA;
      case PANEL_NAMES.ADDRESSES:
        return this.addressesFA;
      case PANEL_NAMES.PHONES:
        return this.phonesFA;
      default:
        return null;
    }
  }

  /**
   * This utility will remove any 'blank' subforms
   * for addresses, emails, phones.
   * The reason for this is so that a user can submit
   * a form without having to delete any
   * emails / phones / addresses which are not filled out.
   * Otherwise, the form would be invalid, for example, because
   * an email address is required.
   *
   * @param panelName The panel to check for "Empty"
   * form array itemms
   */
  private _removeEmptyFCsInPanel(panelName: string) {
    const fa = this._getFAForPanel(panelName);
    let emptyControls: AbstractControl[] = [];
    if (fa === null || fa.controls.length === 0) {
      return;
    }
    if (panelName === PANEL_NAMES.EMAILS) {
      emptyControls = fa.controls.filter(c => isNilOrEmpty((c.value as EmailFormModel)?.email));
    }
    if (panelName === PANEL_NAMES.ADDRESSES) {
      emptyControls = fa.controls.filter(c => {
        const address = c.value as AddressFormModel;
        if (isInternationalCountry(address.country)) {
          const allBlank = [address.streetAddress, address.city, address.zip].every(p => isNilOrEmpty(p));
          return allBlank;
        } else {
          const allBlank =  [address.streetAddress, address.city, address.state, address.zip].every(p => isNilOrEmpty(p));
          return allBlank;
        }
      });
    }

    if (panelName === PANEL_NAMES.PHONES) {
      emptyControls = fa.controls.filter(c => isNilOrEmpty((c.value as PhoneFormModel)?.number));
    }

    removeControlsFromFormArray(fa, emptyControls);
  }
}
